import { useMemo, useCallback } from "react";
import { normalizeDisplayValue } from "./filterHiddenDatapoints";
import { normalizeValue } from "../WidgetBarChart/utils/hiddenDatapoints";
import { getHiddenForPresetAtDepth } from "../WidgetBarChart/utils/getTopBottomDatapoints";
import {
  getVisibilityKeys,
  readVisibilityState,
  resolveHidden,
  writeManualSelection,
  writeDynamicSettings,
  writeMode,
} from "./visibilityState";
import { createVisibilityLightboxContent, getVisibilityModifierLabel } from "../WidgetBarChart/ConfigForm/ShowHideOptions";

/**
 * The rows of the Show/Hide (eye) lightbox: one {display, value} option per
 * datapoint the chart's fetched data holds at the given dimension depth
 * (depth 1 = top-level bars/rows, depth 2+ = children, e.g. breakdown
 * segments or a number field's buckets). Blank-caption datapoints are
 * skipped so they don't render as empty rows.
 *
 * `value` is the datapoint's REAL identifier (e.g. a tag's _id), never its
 * display name — two datapoints sharing a name ("Positive") must stay
 * independently selectable. Display is for rendering only. Falls back to the
 * display string only when the datapoint carries no value at all.
 */
const extractOptionsForDimension = (data, dimension) => {
  if (!data?.length) return [];

  const toOption = (item) => {
    const nd = normalizeDisplayValue(item.display);
    if (nd === "") return null;
    const nv = normalizeValue(item.value);
    return { display: nd, value: nv !== "" ? nv : nd };
  };

  if (dimension === 1) {
    return data.reduce((acc, d) => {
      const opt = toOption(d);
      if (opt) acc.push(opt);
      return acc;
    }, []);
  }

  // Deeper dims aggregate across all parents; dedupe by VALUE — deduping by
  // display is what used to merge same-named tags into one row.
  const byValue = new Map();
  const collectAtDepth = (items, currentDepth, targetDepth) => {
    if (!items) return;
    items.forEach(item => {
      if (currentDepth === targetDepth) {
        const opt = toOption(item);
        if (opt && !byValue.has(opt.value)) byValue.set(opt.value, opt);
      } else {
        collectAtDepth(item.children, currentDepth + 1, targetDepth);
      }
    });
  };
  collectAtDepth(data, 1, dimension);
  return Array.from(byValue.values());
};

/**
 * Builds a visibility modifier config object for a DataFieldInputSelector.
 *
 * @param {Object}   params
 * @param {number}   params.dimension             — dimension depth (1, 2, 3, ...)
 * @param {Object}   params.widgetConfig           — current widget config
 * @param {Object}   params.dataKeys               — DATA_KEYS map
 * @param {Array}    params.data                   — chart data from widgetDataStore
 * @param {boolean}  params.isNumberInput          — whether selected field is NumberInput
 * @param {Function} params.updateConfig           — (updaterFn) => void
 * @param {Object}   [params.keyOverrides]         — override auto-derived key paths { hiddenKey, presetKey, nKey }
 * @param {Array}    [params.optionsOverride]       — custom visibility options [{display, value}], skips internal extraction
 * @param {Function} [params.getHiddenForPreset]   — custom preset function (data, preset, n) => string[]
 * @param {boolean}  [params.enableForNumberInput] — by default number fields get no eye (their dim section owns none);
 *                                                   NumberFieldConfig sets this true because its eye IS for the number
 *                                                   field's buckets/occurrence values.
 *
 * Write seam, for a widget whose manual selection lives somewhere other than the
 * hidden list (the metric card stores the SELECTED ids in `selectedFieldValue`,
 * because selection is that card's data definition):
 *
 * @param {Function} [params.onManualChange]        — (selectedItems, allOptions) => void, replaces the default
 *                                                   "write the hidden list" manual write
 * @param {Array}    [params.manualHiddenOverride]  — manual-mode hidden values, when they are derived from that
 *                                                   external store rather than read from `hiddenKey`
 * @param {Object}   [params.labelOverrides]        — { allSelected, noneSelected } for the manual label strings
 * @param {string}   [params.icon]                  — trigger icon (default 'Eye')
 * @param {string}   [params.tooltipText]           — trigger tooltip (default 'Choose values to show')
 * @param {boolean}  [params.loading]               — options still fetching; renders the trigger in a loading state
 * @param {boolean}  [params.renderWhenEmpty]       — return a modifier even with no options, so the widget can ask
 *                                                   for a selection instead of dropping the control
 */
export const useVisibilityModifier = ({
  dimension,
  widgetConfig,
  dataKeys,
  data,
  isNumberInput,
  updateConfig,
  keyOverrides,
  optionsOverride,
  getHiddenForPreset: getHiddenForPresetCustom,
  enableForNumberInput = false,
  onManualChange,
  manualHiddenOverride,
  labelOverrides,
  icon = 'Eye',
  tooltipText = 'Choose values to show',
  loading = false,
  renderWhenEmpty = false,
}) => {
  // Callers pass keyOverrides as an inline object literal, so memoising on its
  // identity would recompute every render. Key off the resolved strings.
  const ovHidden = keyOverrides?.hiddenKey;
  const ovPreset = keyOverrides?.presetKey;
  const ovN = keyOverrides?.nKey;
  const keys = useMemo(
    () => getVisibilityKeys({
      dataKeys,
      dimension,
      keyOverrides: { hiddenKey: ovHidden, presetKey: ovPreset, nKey: ovN },
    }),
    [dataKeys, dimension, ovHidden, ovPreset, ovN]
  );

  const computeHidden = useCallback((d, preset, n) => {
    if (getHiddenForPresetCustom) return getHiddenForPresetCustom(d, preset, n);
    return getHiddenForPresetAtDepth(d, preset, n, dimension);
  }, [getHiddenForPresetCustom, dimension]);

  const numberInputExcluded = isNumberInput && !enableForNumberInput;

  const visibilityOptions = useMemo(() => {
    if (optionsOverride) return optionsOverride;
    if (!data?.length || numberInputExcluded) return [];
    return extractOptionsForDimension(data, dimension);
  }, [optionsOverride, data, numberInputExcluded, dimension]);

  const state = useMemo(
    () => readVisibilityState(widgetConfig, keys),
    [widgetConfig, keys]
  );

  const effectiveHidden = useMemo(() => {
    // A caller owning the manual write also owns the manual read.
    if (state.mode !== "dynamic" && manualHiddenOverride) return manualHiddenOverride;
    return resolveHidden({ state, data, dimension, computeHidden });
  }, [state, data, dimension, computeHidden, manualHiddenOverride]);

  const handleManualChange = useCallback((newHidden) => {
    if (onManualChange) {
      const hiddenSet = new Set((newHidden || []).map(String));
      onManualChange(
        visibilityOptions.filter((o) => !hiddenSet.has(String(o.value))),
        visibilityOptions
      );
      return;
    }
    updateConfig((w) => writeManualSelection(w, keys, newHidden));
  }, [onManualChange, visibilityOptions, updateConfig, keys]);

  const handleDynamicChange = useCallback((dynamicPreset, n) => {
    updateConfig((w) => writeDynamicSettings(w, keys, { dynamicPreset, n }));
  }, [updateConfig, keys]);

  // Tab switch: only records WHICH state drives the chart. Neither the manual
  // list nor the dynamic settings are touched, so switching back is lossless.
  const handleModeChange = useCallback((mode) => {
    updateConfig((w) => writeMode(w, keys, mode));
  }, [updateConfig, keys]);

  const modifier = useMemo(() => {
    if (numberInputExcluded) return undefined;
    if (!visibilityOptions.length && !renderWhenEmpty) return undefined;
    return {
      icon,
      label: () => getVisibilityModifierLabel(visibilityOptions, effectiveHidden, state.mode, state.n, state.dynamicPreset, labelOverrides),
      options: visibilityOptions,
      loading,
      tooltip: { text: tooltipText },
      lightboxWidth: 320,
      renderLightbox: createVisibilityLightboxContent({
        options: visibilityOptions,
        hiddenValues: effectiveHidden,
        mode: state.mode,
        dynamicPreset: state.dynamicPreset,
        nValue: state.n,
        onManualChange: handleManualChange,
        onDynamicChange: handleDynamicChange,
        onModeChange: handleModeChange,
      }),
    };
  }, [numberInputExcluded, renderWhenEmpty, visibilityOptions, effectiveHidden, state, icon, tooltipText, loading, labelOverrides, handleManualChange, handleDynamicChange, handleModeChange]);

  return modifier;
};
